// Correcting a cost already added (Costs page): its details, its amounts, and how much of it goes on each of its budget
// lines, typed as when it was added (gross, in the cost's currency). Which lines it is on stays: to move it, delete it
// and add it again. Saved by invoice.update, which works the lines' actuals out again.
import { h, useState } from '../core/react.js';
import { T } from '../core/theme.js';
import { btnStyle, inputStyle } from '../ui/widgets.js';
import { numStr } from '../core/doc-upload.js';

export function CostEditor({ inv, colSpan, lineLabel, onSave, onCancel }) {
  const receipt = inv.kind === 'receipt';
  const [f, setF] = useState(() => ({
    supplier: inv.supplier || '', number: inv.number || '', date: inv.date || '', due: inv.due || '', description: inv.description || '',
    notes: inv.notes || '', paidBy: inv.paidBy || '', method: inv.method || '', net: numStr(inv.net), vat: numStr(inv.vat), gross: numStr(inv.gross),
    lines: (inv.allocations || []).map(a => numStr(a.gross))
  }));
  const [saving, setSaving] = useState(false);
  const field = (label, k, props = {}) => h("label", { style: { display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 } },
    h("span", { style: { fontSize: 10.5, color: T.slate, fontWeight: 600 } }, label),
    h("input", { value: f[k], onChange: e => setF({ ...f, [k]: e.target.value }), style: { ...inputStyle, fontSize: 12, padding: '6px 8px', width: '100%', boxSizing: 'border-box' }, ...props }));
  async function save() {
    setSaving(true);
    const { lines, ...rest } = f;
    const ok = await onSave({ ...rest, allocations: lines.map(g => ({ gross: g })) });
    if (!ok) setSaving(false);
  }
  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 };
  return h("tr", null, h("td", { colSpan, style: { padding: '12px 14px 16px', background: T.ivory, borderTop: `1px solid ${T.rule}`, borderBottom: `1px solid ${T.rule}` } },
    h("div", { style: { fontSize: 13, fontWeight: 600, color: T.coal, marginBottom: 10 } }, `Edit ${receipt ? 'receipt' : 'invoice'} ${inv.number || inv.id}`),
    h("div", { style: grid },
      field(receipt ? 'Paid to' : 'Supplier', 'supplier', { autoFocus: true }),
      field(receipt ? 'Receipt no.' : 'Invoice no.', 'number'),
      field('Date', 'date', { type: 'date' }),
      !receipt && field('Due', 'due', { type: 'date' }),
      receipt && field('Paid by', 'paidBy'),
      receipt && field('Payment method', 'method'),
      field(`Net (${inv.ccy})`, 'net', { inputMode: 'decimal' }),
      field(`VAT (${inv.ccy})`, 'vat', { inputMode: 'decimal' }),
      field(`Gross (${inv.ccy})`, 'gross', { inputMode: 'decimal' })),
    h("div", { style: { ...grid, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', marginTop: 10 } },
      field('For', 'description'), field('Notes', 'notes')),
    h("div", { style: { fontSize: 10.5, color: T.slate, fontWeight: 600, margin: '12px 0 4px' } }, `On each budget line (gross, ${inv.ccy})`),
    (inv.allocations || []).map((a, i) => h("label", { key: i, style: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: T.coal, marginBottom: 4 } },
      h("span", { style: { flex: 1, minWidth: 0 } }, lineLabel(a)),
      h("input", { value: f.lines[i], inputMode: 'decimal', 'aria-label': `Amount on ${a.desc}`, onChange: e => setF({ ...f, lines: f.lines.map((x, j) => j === i ? e.target.value : x) }),
        style: { ...inputStyle, fontSize: 12, padding: '5px 8px', width: 120, textAlign: 'right' } }))),
    h("div", { style: { fontSize: 11, color: T.mist, margin: '6px 0 10px' } }, 'To put it on other budget lines, delete it and add it again.'),
    h("div", { style: { display: 'flex', gap: 8 } },
      h("button", { onClick: save, disabled: saving, style: btnStyle(true) }, saving ? 'Saving…' : 'Save changes'),
      h("button", { onClick: onCancel, disabled: saving, style: btnStyle(false) }, 'Cancel'))));
}
